/**
 * Курсы — рабочее место управляющего (§9.5, §11.2 ТЗ сервиса).
 *
 * Экрана не существовало до 12.09.2026, и это был не пробел в отделке, а
 * дыра: на сервере семь адресов для курсов написаны и проверены тестами
 * (создать, модуль, урок, вопросы, опубликовать, назначить, снять), а
 * нажать на них было нечем. Пустой раздел «Обучение» при этом советовал
 * «создать курс в разделе Наполнение» — то есть отправлял туда, где этого
 * сделать нельзя.
 *
 * Три правила, по которым он устроен.
 *
 * 1. **Назначение — главное действие, а не создание.** Курс, который
 *    никому не назначен, не существует для сотрудника: список у него
 *    пустой. Поэтому кому назначено видно в строке курса, до открытия.
 * 2. **Число уроков рядом с названием.** Курс без уроков назначать
 *    нечего — сотрудник откроет его и увидит пустоту. Сервер присылает
 *    счётчик, экран не считает сам.
 * 3. **«Обязательный» — отдельная галочка, и по умолчанию она снята.**
 *    Обязательный курс попадает в статус аттестации (§9.6) и в
 *    напоминания; ставить это молча нельзя.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Field, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { AdminCourseRow } from '../api';
import { CourseContentEditor } from './CourseContent';

/** Черновик нового курса: название и проходной балл. */
function NewCourse({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [passScore, setPassScore] = useState('70');
  const [problem, setProblem] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      studyApi.createCourse({
        title: title.trim(),
        description: description.trim() || null,
        pass_score: Number(passScore) || 70,
      }),
    onSuccess: () => {
      setTitle('');
      setDescription('');
      setPassScore('70');
      setOpen(false);
      setProblem(null);
      onDone();
    },
    // Причину отказа пишет сервер: проходной балл ниже 70 отклоняет СУБД
    // (§9.3), и придумывать здесь свою формулировку значило бы иметь две.
    onError: (error: unknown) =>
      setProblem(error instanceof Error ? error.message : 'Не получилось создать курс.'),
  });

  if (!open) {
    return (
      <Button variant="primary" onClick={() => setOpen(true)}>
        + Новый курс
      </Button>
    );
  }

  return (
    <Card title="Новый курс">
      <Field label="Название">
        <input
          className="search"
          value={title}
          autoFocus
          placeholder="Например: Стандарты сервиса"
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>
      <Field label="О чём курс (необязательно)">
        <input
          className="search"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field label="Проходной балл, %">
        <input
          className="search"
          inputMode="numeric"
          value={passScore}
          onChange={(event) => setPassScore(event.target.value)}
        />
      </Field>
      <p className="faint">
        Ниже 70 % система не примет: это нижняя граница, а не настройка.
      </p>
      {problem && <p className="demo-notice">{problem}</p>}
      <div className="chips">
        <Button
          variant="primary"
          disabled={!title.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Создать черновик
        </Button>
        <Button variant="quiet" onClick={() => setOpen(false)}>
          Отмена
        </Button>
      </div>
    </Card>
  );
}

/** Уроки и тесты курса — раскрываются по кнопке, чтобы список курсов
 *  оставался списком (D98). До 15.09.2026 здесь была форма «+ Урок»,
 *  которая заводила модуль на каждый урок с одним порядком, и второй урок
 *  отклоняла база. */
function ContentToggle({ course, onDone }: { course: AdminCourseRow; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button small variant={open ? 'primary' : 'quiet'} onClick={() => setOpen(!open)}>
        {open ? 'Свернуть уроки' : 'Уроки и тесты'}
      </Button>
      {open && <CourseContentEditor courseId={course.id} onChanged={onDone} />}
    </>
  );
}

/** Кому назначен курс и кому назначить. Должности приходят с сервера тем
 *  же ответом, что курсы. */
function Assignments({
  course,
  positions,
  onDone,
}: {
  course: AdminCourseRow;
  positions: { id: number; name: string }[];
  onDone: () => void;
}) {
  const [mandatory, setMandatory] = useState(false);
  // Срок в днях (D99): у каждого сотрудника своя дата — от назначения или
  // приёма на работу, что позже. По умолчанию срока нет.
  const [dueDays, setDueDays] = useState<number | null>(null);
  const assign = useMutation({
    mutationFn: (positionId: number) =>
      studyApi.assignCourse(course.id, positionId, mandatory, dueDays),
    onSuccess: onDone,
  });
  const remove = useMutation({
    mutationFn: (assignmentId: number) => studyApi.unassign(assignmentId),
    onSuccess: onDone,
  });

  const assigned = new Set(
    course.assignments.map((item) => item.position_id).filter((id): id is number => id !== null),
  );

  return (
    <>
      <p>
        <strong>Кому назначен: </strong>
        {course.assignments.length === 0 ? (
          <span className="faint">никому — сотрудники этот курс не видят</span>
        ) : (
          course.assignments.map((item) => (
            <span key={item.id} className="assignment">
              {item.title}
              {item.is_mandatory ? ' (обязательный)' : ''}
              {item.due_days ? `, срок ${item.due_days} дн.` : ''}
              <button
                type="button"
                className="assignment__remove"
                title="Снять назначение"
                onClick={() => remove.mutate(item.id)}
              >
                ×
              </button>
            </span>
          ))
        )}
      </p>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={mandatory}
          onChange={(event) => setMandatory(event.target.checked)}
        />
        Назначать обязательным — попадёт в статус аттестации и в напоминания
      </label>

      <div className="chips" role="group" aria-label="Срок прохождения">
        <span className="faint">Срок:</span>
        {[null, 7, 14, 30].map((days) => (
          <Button
            key={days ?? 'none'}
            small
            variant={dueDays === days ? 'primary' : 'quiet'}
            onClick={() => setDueDays(days)}
          >
            {days === null ? 'без срока' : `${days} дней`}
          </Button>
        ))}
      </div>
      {dueDays !== null && (
        <p className="faint">
          Отсчёт — от назначения, а у принятых позже — от первого рабочего дня. Напоминание придёт
          за три дня, в день срока и через неделю после, если курс не пройден.
        </p>
      )}

      <div className="chips">
        {positions
          .filter((position) => !assigned.has(position.id))
          .map((position) => (
            <Button
              key={position.id}
              small
              variant="quiet"
              disabled={assign.isPending}
              onClick={() => assign.mutate(position.id)}
            >
              + {position.name}
            </Button>
          ))}
      </div>
    </>
  );
}

export function EditorCourses() {
  const queryClient = useQueryClient();
  const courses = useQuery({ queryKey: ['admin-courses'], queryFn: studyApi.adminCourses });
  const publish = useMutation({
    mutationFn: (courseId: number) => studyApi.publishCourse(courseId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-courses'] }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-courses'] });
    // Список у сотрудника собирается из назначений — он тоже устарел.
    void queryClient.invalidateQueries({ queryKey: ['courses'] });
  };

  if (courses.isPending) return <Loading />;
  if (courses.error || !courses.data)
    return <ErrorState error={courses.error} onRetry={() => courses.refetch()} />;

  return (
    <>
      <NewCourse onDone={refresh} />

      {courses.data.courses.length === 0 ? (
        <Empty
          title="Курсов пока нет"
          text="Создайте курс, добавьте в него уроки и назначьте должностям — после этого он появится у сотрудников в разделе «Учёба»."
        />
      ) : (
        courses.data.courses.map((course) => (
          <Card key={course.id} title={course.title}>
            {course.is_demo && <p className="demo-notice">Демонстрационный курс</p>}
            <p className="faint">
              {course.status === 'published' ? 'опубликован' : 'черновик'} · уроков{' '}
              {course.lessons} · проходной балл {course.pass_score} %
            </p>

            {course.lessons === 0 && (
              <p className="faint">
                В курсе нет ни одного урока — сотруднику будет нечего открыть.
              </p>
            )}

            <ContentToggle course={course} onDone={refresh} />

            {course.status !== 'published' ? (
              <div className="chips">
                <Button
                  small
                  variant="primary"
                  disabled={course.lessons === 0 || publish.isPending}
                  onClick={() => publish.mutate(course.id)}
                >
                  Опубликовать
                </Button>
                {course.lessons === 0 && (
                  <span className="faint">публикуется курс, в котором есть урок</span>
                )}
              </div>
            ) : (
              <Assignments
                course={course}
                positions={courses.data.positions}
                onDone={refresh}
              />
            )}
          </Card>
        ))
      )}
    </>
  );
}
